import { classNames } from '~/helpers/string'

export type Variant = 'error' | 'success' | 'warning' | 'progress' | 'generic'
type AlertTagProps = {
  label: string
  variant?: Variant
  icon?: boolean
  className?: string
}
function AlertTag(props: AlertTagProps) {
  const { variant = 'generic', label, icon = false, className = '' } = props
  let classes = ''
  let Icon = null
  switch (variant) {
    case 'error':
      classes = 'bg-brand-error-bg border-brand-error-stroke text-brand-actions-error'
      Icon = ErrorIcon
      break
    case 'success':
      classes = 'bg-brand-success-bg border-brand-success-stroke text-brand-actions-success'
      Icon = SuccessIcon
      break
    case 'warning':
      classes = 'bg-brand-warning-bg border-brand-warning-stroke text-brand-actions-warning'
      Icon = WarningIcon
      break
    case 'progress':
      classes = 'bg-brand-progress-bg border-brand-progress-stroke text-brand-actions-progress'
      Icon = ProgressIcon
      break
    case 'generic':
      classes = 'bg-brand-generic-bg border-brand-generic-stroke text-brand-actions-generic'
      Icon = GenericIcon
      break
    default:
      throw new Error(`Invalid variant`)
  }
  return (
    <div
      className={classNames(
        'inline-flex h-6 items-center justify-center gap-1 rounded border border-solid px-2 py-1 text-xs font-normal',
        classes,
        className,
      )}
    >
      {icon ? (
        <div>
          <Icon />
        </div>
      ) : null}
      <span className="text-center leading-4 tracking-normal text-inherit">{label}</span>
    </div>
  )
}

export default AlertTag

const ErrorIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.08386 4.59054C4.94763 4.45431 4.72676 4.45431 4.59053 4.59054C4.4543 4.72677 4.4543 4.94764 4.59053 5.08387L5.50666 6L4.59054 6.91612C4.45431 7.05235 4.45431 7.27322 4.59054 7.40945C4.72677 7.54568 4.94764 7.54568 5.08387 7.40945L5.99999 6.49333L6.9161 7.40944C7.05233 7.54567 7.2732 7.54567 7.40943 7.40944C7.54566 7.27321 7.54566 7.05234 7.40943 6.91611L6.49332 6L7.40944 5.08388C7.54567 4.94765 7.54567 4.72678 7.40944 4.59055C7.27321 4.45432 7.05234 4.45432 6.91611 4.59055L5.99999 5.50667L5.08386 4.59054Z"
      fill="#7F1D1D"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1ZM1.69767 6C1.69767 3.62389 3.62389 1.69767 6 1.69767C8.37611 1.69767 10.3023 3.62389 10.3023 6C10.3023 8.37611 8.37611 10.3023 6 10.3023C3.62389 10.3023 1.69767 8.37611 1.69767 6Z"
      fill="#7F1D1D"
    />
  </svg>
)

const SuccessIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.87457 5.08387C8.0108 4.94765 8.0108 4.72677 7.87457 4.59054C7.73834 4.45431 7.51747 4.45431 7.38124 4.59054L5.30233 6.66946L4.61876 5.98589C4.48253 5.84966 4.26166 5.84966 4.12543 5.98589C3.9892 6.12212 3.9892 6.34299 4.12543 6.47922L5.05566 7.40946C5.19189 7.54569 5.41276 7.54569 5.54899 7.40946L7.87457 5.08387Z"
      fill="#14532D"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1ZM1.69767 6C1.69767 3.62389 3.62389 1.69767 6 1.69767C8.37611 1.69767 10.3023 3.62389 10.3023 6C10.3023 8.37611 8.37611 10.3023 6 10.3023C3.62389 10.3023 1.69767 8.37611 1.69767 6Z"
      fill="#14532D"
    />
  </svg>
)

const WarningIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 3.32558C6.19266 3.32558 6.34884 3.48176 6.34884 3.67442V6.46512C6.34884 6.65777 6.19266 6.81395 6 6.81395C5.80734 6.81395 5.65116 6.65777 5.65116 6.46512V3.67442C5.65116 3.48176 5.80734 3.32558 6 3.32558Z"
      fill="#713F12"
    />
    <path
      d="M6 8.32558C6.25688 8.32558 6.46512 8.11734 6.46512 7.86047C6.46512 7.60359 6.25688 7.39535 6 7.39535C5.74312 7.39535 5.53488 7.60359 5.53488 7.86047C5.53488 8.11734 5.74312 8.32558 6 8.32558Z"
      fill="#713F12"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6ZM6 1.69767C3.62389 1.69767 1.69767 3.62389 1.69767 6C1.69767 8.37611 3.62389 10.3023 6 10.3023C8.37611 10.3023 10.3023 8.37611 10.3023 6C10.3023 3.62389 8.37611 1.69767 6 1.69767Z"
      fill="#713F12"
    />
  </svg>
)

const ProgressIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.65116 1.34884C5.65116 1.15618 5.80734 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 4.60138 1.57472 3.33635 2.49998 2.4293C2.63756 2.29443 2.85842 2.29663 2.99329 2.4342C3.12816 2.57178 3.12596 2.79264 2.98839 2.92751C2.19146 3.70875 1.69767 4.7964 1.69767 6C1.69767 8.37611 3.62389 10.3023 6 10.3023C8.37611 10.3023 10.3023 8.37611 10.3023 6C10.3023 3.62389 8.37611 1.69767 6 1.69767C5.80734 1.69767 5.65116 1.54149 5.65116 1.34884Z"
      fill="#2B3184"
    />
    <path
      d="M5.65116 2.74419C5.65116 2.55153 5.80734 2.39535 6 2.39535C7.99079 2.39535 9.60465 4.00921 9.60465 6C9.60465 7.99079 7.99079 9.60465 6 9.60465C4.00921 9.60465 2.39535 7.99079 2.39535 6C2.39535 5.80734 2.55153 5.65116 2.74419 5.65116C2.93684 5.65116 3.09302 5.80734 3.09302 6C3.09302 7.60548 4.39452 8.90698 6 8.90698C7.60548 8.90698 8.90698 7.60548 8.90698 6C8.90698 4.39452 7.60548 3.09302 6 3.09302C5.80734 3.09302 5.65116 2.93684 5.65116 2.74419Z"
      fill="#2B3184"
    />
    <path
      d="M6 3.7907C5.80734 3.7907 5.65116 3.94688 5.65116 4.13953C5.65116 4.33219 5.80734 4.48837 6 4.48837C6.83485 4.48837 7.51163 5.16515 7.51163 6C7.51163 6.83485 6.83485 7.51163 6 7.51163C5.80734 7.51163 5.65116 7.66781 5.65116 7.86047C5.65116 8.05312 5.80734 8.2093 6 8.2093C7.22016 8.2093 8.2093 7.22016 8.2093 6C8.2093 4.77984 7.22016 3.7907 6 3.7907Z"
      fill="#2B3184"
    />
  </svg>
)

const GenericIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M8.25 1.125C7.35254 1.125 6.625 1.85254 6.625 2.75C6.625 2.84795 6.63367 2.94388 6.65028 3.03706L4.28495 4.69279C4.27293 4.7012 4.26156 4.71019 4.25084 4.7197C3.97496 4.50373 3.62752 4.375 3.25 4.375C2.35254 4.375 1.625 5.10254 1.625 6C1.625 6.89746 2.35254 7.625 3.25 7.625C3.62752 7.625 3.97496 7.49627 4.25084 7.2803C4.26156 7.28981 4.27293 7.2988 4.28495 7.30721L6.65028 8.96294C6.63367 9.05612 6.625 9.15205 6.625 9.25C6.625 10.1475 7.35254 10.875 8.25 10.875C9.14746 10.875 9.875 10.1475 9.875 9.25C9.875 8.35254 9.14746 7.625 8.25 7.625C7.72361 7.625 7.25567 7.87529 6.95871 8.26335L4.71903 6.69557C4.81904 6.48471 4.875 6.24889 4.875 6C4.875 5.75111 4.81904 5.51529 4.71903 5.30443L6.95871 3.73665C7.25567 4.12471 7.72361 4.375 8.25 4.375C9.14746 4.375 9.875 3.64746 9.875 2.75C9.875 1.85254 9.14746 1.125 8.25 1.125ZM7.375 2.75C7.375 2.26675 7.76675 1.875 8.25 1.875C8.73325 1.875 9.125 2.26675 9.125 2.75C9.125 3.23325 8.73325 3.625 8.25 3.625C7.76675 3.625 7.375 3.23325 7.375 2.75ZM3.25 5.125C2.76675 5.125 2.375 5.51675 2.375 6C2.375 6.48325 2.76675 6.875 3.25 6.875C3.73325 6.875 4.125 6.48325 4.125 6C4.125 5.51675 3.73325 5.125 3.25 5.125ZM8.25 8.375C7.76675 8.375 7.375 8.76675 7.375 9.25C7.375 9.73325 7.76675 10.125 8.25 10.125C8.73325 10.125 9.125 9.73325 9.125 9.25C9.125 8.76675 8.73325 8.375 8.25 8.375Z"
      fill="#111827"
    />
  </svg>
)
